import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '../../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  Divider,
  RowFlexContainer,
} from '../../../../stories/atoms/containerV2/ContainerV2';
import { useThemeContext } from '../../../../global-state/themeContext';
import { calculateIndexValue, calculateTrend } from '../../../../utils/budgetPlanned';
import { homeTranslations } from '../../../../translations';
import { IndexTypography } from './PerformanceDrawerContent.styles';

export type PerformanceMetricItem = {
  label: string;
  currentValue: number;
  currentValueFormatted: string;
  isEffectiveHours: boolean;
  lastWeek?: number;
  lastWeekFormatted?: string;
  lastYear?: number;
  lastYearFormatted?: string;
};

type PerformanceDrawerContentProps = {
  title: string;
  items: PerformanceMetricItem[];
};

type ComparisonCardItemProps = {
  label: string;
  current: number;
  compared: number;
  formattedValue: string;
  isEffectiveHours: boolean;
};

const ComparisonCardItem: FC<ComparisonCardItemProps> = ({
  label,
  current,
  compared,
  formattedValue,
  isEffectiveHours,
}) => {
  const { mode } = useThemeContext();
  const indexValue = calculateIndexValue(current, compared);
  const trend = calculateTrend(current, compared, isEffectiveHours);

  return (
    <ColumnFlexContainer
      flex={1}
      gap={[1]}
      padding={[1, 2]}
      borderRadius={[2]}
      backgroundColor={mode === 'dark' ? 'backgroundSelectedDark' : 'backgroundTableHeader'}
    >
      <Typography type="caption" color="secondaryTextGray" margin={[0, 0, 1]}>
        {label}
      </Typography>
      <RowFlexContainer justifyContent="between" alignItems="center">
        <IndexTypography type="body3" trend={trend}>
          {indexValue}
        </IndexTypography>
        <Typography type="caption">{formattedValue}</Typography>
      </RowFlexContainer>
    </ColumnFlexContainer>
  );
};

export const PerformanceDrawerContent: FC<PerformanceDrawerContentProps> = ({ title, items }) => {
  const { t } = useTranslation();
  const { common } = homeTranslations;

  const lwLabel = t(common.lastWeek.key, common.lastWeek.defaultValue);
  const lyLabel = t(common.lastYear.key, common.lastYear.defaultValue);

  return (
    <ColumnFlexContainer>
      <Typography type="button">{title}</Typography>
      <Divider margin={[2, 0, 0]} />

      {items.map((item) => (
        <ContainerV2 key={item.label} display="flex" flexDirection="column" gap={[2]} padding={[2, 0]}>
          <ContainerV2 display="flex" justifyContent="between" alignItems="center">
            <Typography type="body3">{item.label}</Typography>
            <Typography type="button">{item.currentValueFormatted}</Typography>
          </ContainerV2>

          {(item.lastWeek !== undefined || item.lastYear !== undefined) && (
            <RowFlexContainer gap={[1]}>
              {item.lastWeek !== undefined && item.lastWeekFormatted && (
                <ComparisonCardItem
                  label={lwLabel}
                  current={item.currentValue}
                  compared={item.lastWeek}
                  formattedValue={item.lastWeekFormatted}
                  isEffectiveHours={item.isEffectiveHours}
                />
              )}
              {item.lastYear !== undefined && item.lastYearFormatted && (
                <ComparisonCardItem
                  label={lyLabel}
                  current={item.currentValue}
                  compared={item.lastYear}
                  formattedValue={item.lastYearFormatted}
                  isEffectiveHours={item.isEffectiveHours}
                />
              )}
            </RowFlexContainer>
          )}
        </ContainerV2>
      ))}
    </ColumnFlexContainer>
  );
};
